import { useState } from "react";
import { Icon, Avatar, Modal, Field, Inp, Sel, Txt, SaveBtn, inputStyle } from "../shared/ui.jsx";
import { USER_COLORS, USERS } from "../shared/constants.js";
import { today } from "../shared/utils.js";

const PROJECTS = ["Litvill Lessons", "Litvill Lab", "Both"];
const PRIORITIES = ["High", "Normal", "Low"];
const PRIORITY_COLORS = { High: "#b05050", Normal: "#7a5c2e", Low: "#8a7a60" };

function getThisYearDate(mmdd) {
  if (!mmdd) return null;
  const year = new Date().getFullYear();
  return `${year}-${mmdd}`;
}

function nextOccurrence(mmdd, frequencyDays) {
  if (!mmdd) return mmdd;
  const year = new Date().getFullYear();
  let date = new Date(`${year}-${mmdd}T12:00:00`);
  const now = new Date();
  // Advance until the date is in the future
  while (date < now) {
    date.setDate(date.getDate() + frequencyDays);
  }
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${m}-${d}`;
}

function isOverdue(mmdd) {
  if (!mmdd) return false;
  const year = new Date().getFullYear();
  const date = new Date(`${year}-${mmdd}T12:00:00`);
  return date < new Date();
}

function formatDeadline(mmdd) {
  if (!mmdd) return "";
  const year = new Date().getFullYear();
  const date = new Date(`${year}-${mmdd}T12:00:00`);
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function daysUntil(mmdd) {
  if (!mmdd) return 999;
  const year = new Date().getFullYear();
  const date = new Date(`${year}-${mmdd}T12:00:00`);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.ceil((date - now) / (1000 * 60 * 60 * 24));
}

export function TodoForm({ onSave, onClose, existing, user, goals = [] }) {
  const todayMmdd = today().slice(5); // MM-DD
  const [form, setForm] = useState(existing || {
    description: "", project: "Litvill Lessons", priority: "Normal",
    recurring: false, frequencyDays: 7,
    deadline: todayMmdd, notes: "", goalId: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const currentYearGoals = goals.filter(g => g.year === new Date().getFullYear());
  const save = () => {
    if (!form.description.trim()) return;
    onSave({
      ...form,
      id: existing?.id || Date.now(),
      createdBy: existing?.createdBy || user,
      createdAt: existing?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: existing?.status || "open",
      doneAt: existing?.doneAt || null,
    });
    onClose();
  };
  return (
    <>
      <Field label="Description *">
        <Inp value={form.description} onChange={e => set("description", e.target.value)} placeholder="Call Dr. Sharma, Buy drill bits…" />
      </Field>
      <Field label="Related goal (optional)">
        <Sel value={form.goalId || ""} onChange={e => set("goalId", e.target.value)}>
          <option value="">— none —</option>
          {currentYearGoals.map(g => <option key={g.id} value={g.id}>{g.description}</option>)}
        </Sel>
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Field label="Project">
          <Sel value={form.project} onChange={e => set("project", e.target.value)}>
            {PROJECTS.map(p => <option key={p}>{p}</option>)}
          </Sel>
        </Field>
        <Field label="Priority">
          <Sel value={form.priority} onChange={e => set("priority", e.target.value)}>
            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </Sel>
        </Field>
      </div>
      <Field label="Deadline (this year)">
        <Inp type="date" value={`${new Date().getFullYear()}-${form.deadline}`}
          onChange={e => set("deadline", e.target.value.slice(5))} />
      </Field>
      <Field label="Task type">
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {[false, true].map(r => (
            <button key={String(r)} type="button" onClick={() => set("recurring", r)} style={{
              flex: 1, padding: "0.5rem", borderRadius: 3, cursor: "pointer", fontSize: "0.85rem",
              border: `2px solid ${form.recurring === r ? "var(--accent)" : "var(--ink20)"}`,
              background: form.recurring === r ? "var(--accent)" : "transparent",
              color: form.recurring === r ? "var(--paper)" : "var(--ink)",
            }}>{r ? "Recurring" : "One-time"}</button>
          ))}
        </div>
      </Field>
      {form.recurring && (
        <Field label={`Repeat every ${form.frequencyDays} day(s)`}>
          <input type="range" min="1" max="30" value={form.frequencyDays}
            onChange={e => set("frequencyDays", Number(e.target.value))}
            style={{ width: "100%", accentColor: "var(--accent)" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem", color: "var(--ink30)", marginTop: 2 }}>
            <span>1 day</span><span>30 days</span>
          </div>
        </Field>
      )}
      <Field label="Notes"><Txt value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Context, waiting for…" /></Field>
      <SaveBtn onClick={save} />
    </>
  );
}

function TodoCard({ item, onEdit, onDelete, onDone, currentUser, goalLabel }) {
  const isOwn = item.createdBy === currentUser;
  const overdue = isOverdue(item.deadline);
  const days = daysUntil(item.deadline);
  const priorityColor = PRIORITY_COLORS[item.priority] || "var(--ink50)";
  const userColor = USER_COLORS[item.createdBy] || "var(--accent)";
  const projectColor = item.project === "Litvill Lessons" ? "#3d6b8a" : item.project === "Litvill Lab" ? "#5b8a6b" : "#7a5c2e";

  const deadlineColor = overdue ? "#b05050" : days <= 3 ? "#c47a30" : "var(--ink50)";
  const deadlineLabel = overdue
    ? `${Math.abs(days)}d overdue`
    : days === 0 ? "Today"
    : days === 1 ? "Tomorrow"
    : `${days}d left`;

  return (
    <div style={{
      padding: "0.85rem 1rem", background: "var(--paper-dark)", borderRadius: 4, marginBottom: "0.5rem",
      border: `1px solid ${overdue ? "#b0505033" : "var(--ink10)"}`,
      borderLeft: `3px solid ${overdue ? "#b05050" : priorityColor}`,
      opacity: 1,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.08em", color: projectColor, fontWeight: 600 }}>{item.project}</span>
            <span style={{ fontSize: "0.65rem", color: priorityColor, fontWeight: 600 }}>· {item.priority}</span>
            {item.recurring && <span style={{ fontSize: "0.65rem", color: "var(--ink30)" }}>· Every {item.frequencyDays}d</span>}
          </div>
          <div style={{ fontFamily: "var(--font-head)", fontSize: "0.95rem", color: overdue ? "#b05050" : "var(--ink)" }}>{item.description}</div>
          {item.notes && <div style={{ fontSize: "0.78rem", color: "var(--ink50)", marginTop: 3, fontStyle: "italic" }}>{item.notes}</div>}
          {goalLabel && <div style={{ fontSize: "0.74rem", color: "var(--accent)", marginTop: 3 }}>🎯 {goalLabel}</div>}
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginTop: 5 }}>
            <Avatar user={item.createdBy} size={14} />
            <span style={{ fontSize: "0.68rem", color: userColor }}>{item.createdBy}</span>
            <span style={{ fontSize: "0.72rem", color: deadlineColor, fontWeight: overdue ? 600 : 400 }}>
              📅 {formatDeadline(item.deadline)} · {deadlineLabel}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.4rem", flexShrink: 0 }}>
          {isOwn && (
            <button onClick={() => onDone(item)} title={item.recurring ? "Mark done & advance date" : "Complete"} style={{
              width: 28, height: 28, borderRadius: "50%", border: `2px solid ${overdue ? "#b05050" : "var(--ink20)"}`,
              background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              color: overdue ? "#b05050" : "var(--ink30)", flexShrink: 0,
            }}>✓</button>
          )}
          <div style={{ display: "flex", gap: 2 }}>
            {isOwn && <button onClick={() => onEdit(item)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink30)", padding: 2 }}><Icon name="edit" size={13} /></button>}
            {isOwn && <button onClick={() => onDelete(item.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink30)", padding: 2 }}><Icon name="trash" size={13} /></button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function ArchiveCard({ item }) {
  const projectColor = item.project === "Litvill Lessons" ? "#3d6b8a" : item.project === "Litvill Lab" ? "#5b8a6b" : "#7a5c2e";
  return (
    <div style={{ padding: "0.6rem 1rem", background: "var(--paper-dark)", borderRadius: 4, marginBottom: "0.4rem", border: "1px solid var(--ink10)", opacity: 0.6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <span style={{ fontSize: "0.65rem", color: projectColor, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{item.project} · </span>
          <span style={{ fontSize: "0.88rem", color: "var(--ink70)", textDecoration: "line-through" }}>{item.description}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          <Avatar user={item.createdBy} size={14} />
          <span style={{ fontSize: "0.68rem", color: "var(--ink30)" }}>{item.doneAt ? new Date(item.doneAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}</span>
        </div>
      </div>
    </div>
  );
}

export default function TodoTab({ user, todos, setTodos, searchQ, goals = [] }) {
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([...USERS]);
  const [filterProject, setFilterProject] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [showArchive, setShowArchive] = useState(false);

  const toggleUser = (u) => setSelectedUsers(prev =>
    prev.includes(u) ? (prev.length > 1 ? prev.filter(x => x !== u) : prev) : [...prev, u]
  );

  const open = (todos || []).filter(t => t.status === "open");
  const archive = (todos || []).filter(t => t.status === "done");

  let items = open.filter(t => selectedUsers.includes(t.createdBy));
  if (searchQ) items = items.filter(t => t.description.toLowerCase().includes(searchQ) || (t.notes || "").toLowerCase().includes(searchQ));
  if (filterProject !== "all") items = items.filter(t => t.project === filterProject);
  if (filterPriority !== "all") items = items.filter(t => t.priority === filterPriority);

  // Sort by deadline — overdue first, then closest
  items = [...items].sort((a, b) => {
    const da = daysUntil(a.deadline);
    const db = daysUntil(b.deadline);
    return da - db;
  });

  const save = (item) => {
    if (editItem) setTodos((todos || []).map(t => t.id === item.id ? item : t));
    else setTodos([...(todos || []), item]);
    setEditItem(null); setShowForm(false);
  };

  const del = (id) => setTodos((todos || []).filter(t => t.id !== id));

  const markDone = (item) => {
    if (item.recurring) {
      // Advance deadline to next occurrence
      const next = nextOccurrence(item.deadline, item.frequencyDays);
      setTodos((todos || []).map(t => t.id === item.id ? { ...t, deadline: next, updatedAt: new Date().toISOString() } : t));
    } else {
      // Archive it
      setTodos((todos || []).map(t => t.id === item.id ? { ...t, status: "done", doneAt: new Date().toISOString() } : t));
    }
  };

  return (
    <div>
      {/* User selector */}
      <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: "0.72rem", color: "var(--ink50)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Show:</span>
        {USERS.map(u => (
          <button key={u} onClick={() => toggleUser(u)} style={{
            display: "flex", alignItems: "center", gap: "0.3rem",
            padding: "0.3rem 0.65rem", borderRadius: 20, cursor: "pointer", fontSize: "0.8rem",
            border: `2px solid ${selectedUsers.includes(u) ? USER_COLORS[u] : "var(--ink20)"}`,
            background: selectedUsers.includes(u) ? USER_COLORS[u] + "18" : "transparent",
            color: selectedUsers.includes(u) ? USER_COLORS[u] : "var(--ink50)",
            fontWeight: selectedUsers.includes(u) ? 600 : 400,
          }}>
            <Avatar user={u} size={16} />{u}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
        <Sel value={filterProject} onChange={e => setFilterProject(e.target.value)} style={{ ...inputStyle, width: "auto", fontSize: "0.8rem" }}>
          <option value="all">All projects</option>
          {PROJECTS.map(p => <option key={p}>{p}</option>)}
        </Sel>
        <Sel value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={{ ...inputStyle, width: "auto", fontSize: "0.8rem" }}>
          <option value="all">All priorities</option>
          {PRIORITIES.map(p => <option key={p}>{p}</option>)}
        </Sel>
      </div>

      <div style={{ fontSize: "0.78rem", color: "var(--ink50)", marginBottom: "0.5rem" }}>
        {items.length} task(s) · {items.filter(t => isOverdue(t.deadline)).length} overdue
      </div>

      {items.length === 0 && <p style={{ color: "var(--ink30)", fontStyle: "italic", fontSize: "0.9rem" }}>No tasks found.</p>}
      {items.map(t => (
        <TodoCard key={t.id} item={t} currentUser={user}
          goalLabel={t.goalId ? goals.find(g => String(g.id) === String(t.goalId))?.description : null}
          onEdit={item => { setEditItem(item); setShowForm(true); }}
          onDelete={del}
          onDone={markDone}
        />
      ))}

      {/* Archive */}
      {archive.length > 0 && (
        <div style={{ marginTop: "1.5rem" }}>
          <button onClick={() => setShowArchive(v => !v)} style={{ display: "flex", alignItems: "center", gap: "0.4rem", background: "none", border: "none", cursor: "pointer", color: "var(--ink50)", fontSize: "0.78rem", padding: "0.2rem 0", marginBottom: "0.5rem" }}>
            <Icon name={showArchive ? "chevronUp" : "chevron"} size={14} />
            Archive ({archive.length} completed tasks)
          </button>
          {showArchive && archive.map(t => <ArchiveCard key={t.id} item={t} />)}
        </div>
      )}

      {showForm && (
        <Modal title={editItem ? "Edit Task" : "New Task"} onClose={() => { setShowForm(false); setEditItem(null); }}>
          <TodoForm user={user} goals={goals} onSave={save} onClose={() => { setShowForm(false); setEditItem(null); }} existing={editItem} />
        </Modal>
      )}
    </div>
  );
}